/**
 * The ship builder.
 *
 * Looking straight down at your own berth, with the station's deck stretching
 * away around it. Not an abstract canvas: the pad is the build volume, so how
 * large a ship you can make is a fact about where you docked, and the clutter
 * around it is what tells you how big the thing you are drawing really is.
 *
 * Lines snap to a quarter-unit grid; components do not, because they turn in
 * eighths and a grid-locked diagonal would sit wrong against its own hull.
 *
 * Every rule lives in `@interspacial/shared` — which spaces the lines enclose,
 * whether a component fits, whether you own a line long enough, what a joint
 * costs. This file is the hands: pointer maths, meshes, and what to draw in red.
 */

import {
  COMPONENT_SPECS,
  COMPONENT_TYPES,
  GRID,
  ROTATION_STEP,
  checkPlacement,
  componentOutline,
  drawScaleOf,
  renderFacing,
  computeShipPerformance,
  activeComponentTypes,
  findCompartments,
  fitLine,
  floatingEnds,
  boundsOf,
  getStationGeometry,
  getSystem,
  jointCostOf,
  jointsInUse,
  exportDesign,
  importDesign,
  LINE_GRADES,
  LINE_HEALTH,
  canAfford,
  formatLinelits,
  heldComponents,
  heldJoints,
  heldLines,
  LINELITS_PER_UNIT,
  MIN_LINE_LENGTH,
  type Fit,
  stow,
  take,
  takeComponent,
  takeJoint,
  type CargoItem,
  repairCost,
  lineRepairCost,
  spend,
  type Wallet,
  PAINTS,
  gradeOfStrength,
  platingCost,
  PAINT_COST,
  stationStock,
  cargoCapacity,
  lineWeight,
  mirrorComponent,
  mirrorPoint,
  mirrorSegment,
  totalSurfaceArea,
  type ComponentType,
  type Joint,
  type Line,
  type ShipComponent,
  type ShipGraph,
  type StockLine,
  type SystemId,
  REPAIR_STEPS,
} from '@interspacial/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { SHOW_KEY_HINTS, TOUCH_UI, hudScaled } from './hudScale';
import * as THREE from 'three';
import { mountViewport } from './viewport';

import { GAME_SCALE, buildStationDeck } from './systemScene';
import { loadComponentGeometry } from './models';
import { createStructureMaterial, withEdges } from './shaders/structure';

/** How much of the deck to show beyond the pad itself. */
const VIEW_MARGIN = 2.2;

/**
 * How far above the station the ship's components are drawn.
 *
 * They keep their own depth testing, unlike the flat overlays, so they need to
 * clear the deck and its masts by enough that nothing on the station occludes
 * them. Only the relative order matters here — the view is orthographic, so
 * lifting them costs no perspective.
 */
const MESH_LIFT = 400;

/**
 * Draw this over the station, unconditionally.
 *
 * Height is not a workable way to arrange that. The deck is several world units
 * thick once the game scale is applied and its masts stand well above that, so
 * anything placed at a plausible-looking height ends up buried inside the very
 * station it is being drawn on — which is exactly what happened to the grid and
 * every line the editor drew.
 *
 * Depth testing is switched off instead and the order stated explicitly. This
 * is a flat top-down editor; there is no depth relationship worth preserving
 * between the design and the scenery behind it.
 */
/**
 * Put something on a numbered layer.
 *
 * Depth testing off, so what covers what is decided entirely by `order` — the
 * editor is a flat diagram and everything in it sits at z = 0, where a depth
 * buffer has nothing to say.
 *
 * ── Opaque, always ──────────────────────────────────────────────────────────
 *
 * Transparency is forced off here, and that is the whole reason this function
 * exists rather than a bare `renderOrder =`. Three.js draws every opaque object
 * before any transparent one and sorts the two lists separately, so a
 * transparent material *always* lands on top of an opaque one no matter what
 * order either was given. The grid was translucent and the compartment floors
 * were not, which put graph paper over the entire ship — and no amount of
 * renumbering could have fixed it, because the numbers were never being
 * compared.
 *
 * Anything that wants to look faint bakes that into its colour instead. There
 * is one background and it does not move, so a dimmed colour and a translucent
 * one are the same picture.
 */
function asOverlay<T extends THREE.Object3D>(object: T, order: number): T {
  object.traverse((child) => {
    const mesh = child as THREE.Mesh;
    const material = mesh.material as THREE.Material | THREE.Material[] | undefined;
    for (const one of Array.isArray(material) ? material : material ? [material] : []) {
      one.depthTest = false;
      one.depthWrite = false;
      one.transparent = false;
      one.opacity = 1;
    }
    child.renderOrder = order;
  });
  object.renderOrder = order;
  return object;
}

/**
 * A colour as it looked when it was translucent over the editor's background.
 *
 * Everything here is opaque now — see `asOverlay` — so "faint" has to be a
 * darker colour rather than a lower alpha.
 */
function dimmed(color: number, amount: number): number {
  return new THREE.Color(color).lerp(new THREE.Color(0x05070d), 1 - amount).getHex();
}

type Tool = 'draw' | 'place' | 'select' | 'erase';

/**
 * How far down the side panels start, clear of the tool bar.
 *
 * The bar's own height, and it moves with the bar: on a touch device the row of
 * tools is drawn at the HUD scale and its padding is tighter, so the panels
 * follow it up rather than leaving a band of nothing between.
 */
const PANEL_TOP = TOUCH_UI ? 40 : 56;

/** A position on the screen, in client coordinates. */
interface Point {
  x: number;
  y: number;
}

/**
 * What a freshly drawn line is made of.
 *
 * Every line has the same health; upgrading buys *strength*, which reduces
 * incoming damage. One axis, no trade-off, and a tougher hull is simply one
 * whose owner has been further afield — see `ship/upgrades.ts`.
 */
const DEFAULT_LINE = {
  health: LINE_HEALTH,
  strength: LINE_GRADES[0]!.strength,
  color: LINE_GRADES[0]!.color,
} as const;

export interface ShipBuilderProps {
  /** Whether to offer the tools that ignore the economy. */
  admin?: boolean;
  seed: string;
  systemId: SystemId;
  stationId: string;
  padId: string;
  design: ShipGraph;
  /**
   * The plan, as against the ship.
   *
   * What the editor draws is the hull — the ship you actually own, holes and
   * all. This is what it is *meant* to be, and it becomes the template: anything
   * in it that the ship no longer has shows as a ghost, so opening the editor on
   * a wreck shows you exactly what came off and lets you build it back.
   *
   * Saved from the hull on the way out, without the damage. That is the whole
   * definition of a blueprint: this shape, undented.
   */
  blueprint?: ShipGraph | null;
  wallet?: Wallet;
  onWalletChange?: (wallet: Wallet) => void;
  /** The hold. Drawing spends line out of it; fitting a part spends the part. */
  cargo?: readonly CargoItem[];
  onCargoChange?: (cargo: CargoItem[]) => void;
  capacity?: number;
  /** Rebuild the hull to the blueprint. Charged for, unlike the fabricator. */
  onRepair?: () => void;
  /**
   * Put one line back, leaving the rest of the hull as it is.
   *
   * Separate from `onRepair` because the bills are different orders of
   * magnitude: damage lands on the walls that were actually in the way, so a
   * scrape is usually one wall, and being quoted for the whole hull to fix it is
   * how a cheap repair gets skipped until it is an expensive one.
   */
  onRepairLine?: (lineId: string) => void;
  /** How much of this station's plating and paint everyone has taken. */
  taken?: Record<string, number>;
  /** Ask the server to do something. See `runAction`. */
  act?: (path: string, body?: Record<string, unknown>) => Promise<boolean>;
  onChange: (design: ShipGraph) => void;
  onExit: () => void;
  /** Lines in cargo. Drawing consumes them, cutting one down when nothing fits. */
  stock?: readonly StockLine[];
  /** Joints in cargo — what it costs to grow a hull outward. */
  jointsOwned?: number;
}


/** A starting stock, so the builder is usable before cargo exists. */
const DEBUG_STOCK: StockLine[] = Array.from({ length: 60 }, (_, i) => ({
  id: `dbg-${i}`,
  length: 1 + (i % 12) * 0.5,
  ...DEFAULT_LINE,
}));

export function ShipBuilder({
  admin = false,
  seed,
  systemId,
  stationId,
  padId,
  design,
  blueprint = null,
  wallet,
  onWalletChange,
  cargo,
  onCargoChange,
  capacity = 0,
  onRepair,
  onRepairLine,
  taken = {},
  act,
  onChange,
  onExit,
  stock,
  jointsOwned = 12,
}: ShipBuilderProps) {
  const mountRef = useRef<HTMLDivElement>(null);

  /**
   * What each line's health actually is.
   *
   * Straight off the graph being edited, because that graph is the hull. There
   * used to be a separate copy passed in beside it and the two could disagree.
   */
  const condition = useMemo(
    () => new Map(design.lines.map((line) => [line.id, line.health])),
    [design],
  );
  const healthOf = (id: string) => condition.get(id) ?? LINE_HEALTH;

  /** Lines with something taken out of them. What the alert counts. */
  const damaged = useMemo(
    () => design.lines.filter((line) => healthOf(line.id) < LINE_HEALTH),
    [design, condition],
  );

  const repairBill = repairCost(design);

  const [tool, setTool] = useState<Tool>('draw');
  const [picked, setPicked] = useState<ComponentType>('pilotSeat');
  const [facing, setFacing] = useState(0);
  /**
   * Build without spending anything from the hold.
   *
   * Off by default and only offered to an admin. It was on for everyone, which
   * quietly made the whole economy optional: line, joints and parts are what
   * cargo is *for*, and a builder that ignores them means nothing you buy ever
   * mattered.
   */
  const [unlimited, setUnlimited] = useState(false);
  /**
   * Symmetry, about the ship's fore-and-aft centreline.
   *
   * Almost every ship anyone designs is symmetric, and hand-building the second
   * half is tedious and easy to get subtly wrong — a thruster a quarter-cell out
   * on one side is invisible here and permanent once flown.
   *
   * One axis only. Ships are built nose-up, so a fore-and-aft mirror is not a
   * thing anyone wants and offering it only invited picking the wrong one.
   */
  const [mirror, setMirror] = useState(false);
  const [hint, setHint] = useState('');
  /** What the select tool is pointing at — a line or a component. */
  const [selected, setSelected] = useState<
    { kind: 'line' | 'component' | 'ghostLine' | 'ghostComponent'; id: string } | null
  >(null);

  /**
   * A design to build *towards*.
   *
   * Anything in the template that is not on the ship is drawn as a ghost, so a
   * template is at once three things with one mechanism: a shape to copy from a
   * friend, a record of what your ship is supposed to be, and a plan you are
   * working through a piece at a time.
   *
   * Starts as the blueprint, which makes the common case free: open the editor
   * on a ship that has lost a wall and the missing wall is already showing,
   * because the plan still has it and the ship does not.
   */
  const [template, setTemplate] = useState<{ name: string; graph: ShipGraph } | null>(() => {
    const plan = blueprint ?? design;
    return plan.lines.length > 0 ? { name: 'Blueprint', graph: plan } : null;
  });

  /**
   * What the template has and the ship does not.
   *
   * Matched by id rather than by geometry: a line you drew in the same place is
   * still not the line the template asked for until it *is* that line, and
   * pretending otherwise would tick items off a plan you had not followed.
   */
  const missing = useMemo(() => {
    if (!template) return { joints: [], lines: [], components: [] };

    const haveJoints = new Set(design.joints.map((j) => j.id));
    const haveLines = new Set(design.lines.map((l) => l.id));
    const haveParts = new Set(design.components.map((c) => c.id));

    return {
      joints: template.graph.joints.filter((j) => !haveJoints.has(j.id)),
      lines: template.graph.lines.filter((l) => !haveLines.has(l.id)),
      components: template.graph.components.filter((c) => !haveParts.has(c.id)),
    };
  }, [template, design]);

  /**
   * What this station has left on the shelf.
   *
   * Seeded from the station's identity, then drawn down as it is spent. Kept in
   * the editor for now — once cargo is real this belongs with the rest of the
   * player's state, since walking out and back in should not restock the place.
   */
  /*
   * Held against the restocking period rather than in local state.
   *
   * The shelf used to be regenerated from the seed every time this panel
   * mounted, so walking out of the editor and back in refilled it — and what
   * had been bought was never recorded anywhere. It is the trader's problem and
   * it gets the trader's answer: the same two-hour window, the same store of
   * what has been taken. See `shelfEra`.
   */
  const shelf = useMemo(() => {
    const stocked = stationStock(seed, stationId);
    const grades: Record<string, number> = {};

    for (const [id, held] of Object.entries(stocked.grades)) {
      grades[id] = Number.isFinite(held) ? Math.max(0, held - (taken[`grade-${id}`] ?? 0)) : held;
    }

    return { grades, paint: Math.max(0, stocked.paint - (taken.paint ?? 0)) };
  }, [seed, stationId, taken]);

  const { joints, lines, components } = design;

  // ── The berth we are building on ───────────────────────────────────────────
  const berth = useMemo(() => {
    const system = getSystem(seed, systemId);
    const station = system.stations.find((s) => s.id === stationId) ?? system.stations[0];
    if (!station) return null;

    const geometry = getStationGeometry(seed, station);
    const pad = geometry.pads.find((p) => p.padId === padId) ?? geometry.pads[0];
    if (!pad) return null;

    return {
      station,
      geometry,
      pad,
      // The pad in world units is the build volume: you cannot construct a ship
      // wider than the berth it has to sit on.
      half: (pad.extent * GAME_SCALE) / 2,
      centre: { x: pad.position.x * GAME_SCALE, y: pad.position.y * GAME_SCALE },
    };
  }, [seed, systemId, stationId, padId]);

  const compartments = useMemo(() => findCompartments(design), [design]);
  const loose = useMemo(() => floatingEnds(design), [design]);

  const report = useMemo(() => {
    /*
     * Named `active` and meaning "all of them", until now.
     *
     * The editor drew the dormancy rule beside every part and then quoted a top
     * speed that counted the dormant ones, so a hull with its thrusters walled
     * in read as fully powered on the one screen that was telling you otherwise.
     */
    const active = activeComponentTypes(design);
    return {
      performance: computeShipPerformance(active),
      area: totalSurfaceArea(compartments),
      jointsUsed: jointsInUse(design),
      problems: components
        .map((component) => ({ component, result: checkPlacement(component, design, compartments) }))
        .filter((entry) => !entry.result.ok),
    };
  }, [components, compartments, design]);

  /**
   * The line the editor may spend.
   *
   * From the hold when there is one, and from the debug shelf otherwise — the
   * builder has to stay usable on its own, and `unlimited` exists for exactly
   * that. Stacks are expanded into individual pieces because `fitLine` chooses
   * the tightest one that will do, and it cannot choose against a count.
   */
  const available = useMemo(
    () => (cargo ? heldLines(cargo) : (stock ?? DEBUG_STOCK)),
    [cargo, stock],
  );

  /** Parts on hand, by type. Undefined when the hold is not wired up. */
  const partsHeld = useMemo(() => (cargo ? heldComponents(cargo) : null), [cargo]);
  const jointsHeld = useMemo(() => (cargo ? heldJoints(cargo) : jointsOwned), [cargo, jointsOwned]);

  // ── Scene ──────────────────────────────────────────────────────────────────
  const sceneRef = useRef<{
    content: THREE.Group;
    overlay: THREE.Group;
    toWorld: (clientX: number, clientY: number) => { x: number; y: number };
    /**
     * Slide the view so the world point under `from` ends up under `to`.
     *
     * In client coordinates rather than world ones, because every caller has a
     * pair of screen positions and none of them wants to do the unprojection
     * twice — and because doing it here is what keeps a drag pinned to the thing
     * it grabbed rather than drifting as the zoom changes underneath it.
     */
    panTo: (from: Point, to: Point) => void;
    /** Zoom about a screen point, leaving whatever is under it where it is. */
    zoomAt: (factor: number, at: Point) => void;
  } | null>(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount || !berth) return;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x05070d);

    let width = mount.clientWidth;
    let height = mount.clientHeight;

    const baseSpan = berth.half * 2 * VIEW_MARGIN;
    const camera = new THREE.OrthographicCamera(0, 0, 0, 0, -100_000, 100_000);
    camera.position.set(0, 0, 10_000);
    camera.lookAt(0, 0, 0);

    /*
     * ── Pan and zoom, declared before the viewport that calls it ─────────────
     *
     * `mountViewport` runs its resize callback *once, immediately*, so that
     * nothing has to duplicate the initial sizing. That means everything the
     * callback touches has to exist by the time the viewport is mounted — and
     * these two used to sit two hundred lines further down, past a wall of scene
     * building.
     *
     * `const` is not hoisted, so the first call reached both of them inside the
     * temporal dead zone and the editor threw before it drew anything. Moving
     * the declaration is the fix rather than deferring the call: the camera does
     * genuinely need framing before the first paint, and everything these depend
     * on — the size, the span and the camera — is already above.
     */
    const view = { x: 0, y: 0, zoom: 1 };

    const frameCamera = () => {
      const aspect = width / height;
      const half = baseSpan / 2 / view.zoom;
      camera.left = -half * aspect;
      camera.right = half * aspect;
      camera.top = half;
      camera.bottom = -half;
      camera.position.x = view.x;
      camera.position.y = view.y;
      camera.updateProjectionMatrix();
      camera.updateMatrixWorld();
    };

    const viewport = mountViewport(mount, (w: number, h: number) => {
      width = w;
      height = h;
      // Re-framed rather than given an aspect: this camera is pointed at a
      // subject, and the subject has to stay in shot at any window size.
      frameCamera();
    });
    const renderer = viewport.renderer;

    // The station, seen from directly above and shifted so the pad is centred.
    // Everything is built in pad-local coordinates from here on, which is also
    // the frame the ship design is stored in.
    const deck = buildStationDeck(berth.geometry, berth.station.allegiance ?? 'unaligned');
    deck.scale.setScalar(GAME_SCALE);
    deck.position.set(-berth.centre.x, -berth.centre.y, 0);
    scene.add(deck);

    scene.add(new THREE.AmbientLight(0xffffff, 0.4));
    const key = new THREE.DirectionalLight(0xffffff, 0.9);
    key.position.set(0.4, -0.6, 1);
    scene.add(key);

    // The build volume.
    scene.add(
      asOverlay(
        new THREE.LineLoop(
          new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(-berth.half, -berth.half, 0),
            new THREE.Vector3(berth.half, -berth.half, 0),
            new THREE.Vector3(berth.half, berth.half, 0),
            new THREE.Vector3(-berth.half, berth.half, 0),
          ]),
          new THREE.LineBasicMaterial({ color: dimmed(0xffd479, 0.7) }),
        ),
        20,
      ),
    );

    // ── Grid ─────────────────────────────────────────────────────────────────
    // Heavier every fourth line — one whole world unit, the scale line lengths
    // are quoted in. Confined to the pad, since that is the buildable area.
    const cells = Math.ceil(berth.half / GRID);
    const fine: number[] = [];
    const coarse: number[] = [];
    const reach = cells * GRID;

    for (let i = -cells; i <= cells; i++) {
      const at = i * GRID;
      (i % 4 === 0 ? coarse : fine).push(-reach, at, 0, reach, at, 0, at, -reach, 0, at, reach, 0);
    }

    const fineMaterial = new THREE.LineBasicMaterial({ color: dimmed(0x2c4a68, 0.35) });

    /*
     * Layers, bottom to top: compartment floors, grid, ghosts, walls, joints,
     * parts, cursors. See `asOverlay` — order is the only thing deciding what
     * covers what, and the numbers are spread out to leave room between them.
     *
     * The grid sits *over* the floors and under everything else. Over, because a
     * floor that hid the grid would take the measure away from the one surface
     * you are measuring on; under, because graph paper drawn across your ship is
     * exactly what this was doing wrong.
     */
    for (const [points, material, order] of [
      [fine, fineMaterial, 10],
      [coarse, new THREE.LineBasicMaterial({ color: dimmed(0x4a7099, 0.75) }), 11],
    ] as const) {
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.Float32BufferAttribute(points, 3));
      scene.add(asOverlay(new THREE.LineSegments(geometry, material), order));
    }

    const content = new THREE.Group();
    const overlay = new THREE.Group();
    scene.add(content, overlay);

    const toWorld = (clientX: number, clientY: number) => {
      const rect = renderer.domElement.getBoundingClientRect();
      const point = new THREE.Vector3(
        ((clientX - rect.left) / rect.width) * 2 - 1,
        -((clientY - rect.top) / rect.height) * 2 + 1,
        0,
      );
      point.unproject(camera);
      return { x: point.x, y: point.y };
    };

    /*
     * Zoom about a point: whatever is under it stays under it.
     *
     * Zooming about the centre of the screen makes closing in on a detail a
     * chore, because the thing you are aiming at slides away as you approach it.
     * The same maths serves a wheel and a pinch — one supplies the cursor and a
     * fixed step, the other the midpoint between two fingers and the ratio they
     * moved by — which is the reason it is a function rather than a handler.
     */
    const zoomAt = (factor: number, at: Point) => {
      const before = toWorld(at.x, at.y);
      view.zoom = THREE.MathUtils.clamp(view.zoom * factor, 0.4, 40);
      frameCamera();
      const after = toWorld(at.x, at.y);

      view.x += before.x - after.x;
      view.y += before.y - after.y;
      frameCamera();
    };

    const panTo = (from: Point, to: Point) => {
      const before = toWorld(from.x, from.y);
      const after = toWorld(to.x, to.y);
      view.x += before.x - after.x;
      view.y += before.y - after.y;
      frameCamera();
    };

    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      zoomAt(event.deltaY < 0 ? 1.15 : 1 / 1.15, { x: event.clientX, y: event.clientY });
    };

    /*
     * Middle and right drag pan; left stays free for the tools.
     *
     * Mouse and pen only. A finger has no middle button — `button` is always 0 —
     * so this never fired on touch and the view could not be moved at all, on a
     * pad several times the size of a phone. Touch pans through the gesture
     * handling in the component instead, which has to tell a drag from a tap
     * before it knows which of the two this even is.
     */
    let panning: Point | null = null;

    const onPanStart = (event: PointerEvent) => {
      if (event.pointerType === 'touch') return;
      if (event.button !== 1 && event.button !== 2) return;
      event.preventDefault();
      panning = { x: event.clientX, y: event.clientY };
      renderer.domElement.setPointerCapture(event.pointerId);
    };

    const onPanMove = (event: PointerEvent) => {
      if (!panning) return;
      const at = { x: event.clientX, y: event.clientY };
      panTo(panning, at);
      panning = at;
    };

    const onPanEnd = () => {
      panning = null;
    };

    renderer.domElement.addEventListener('wheel', onWheel, { passive: false });
    renderer.domElement.addEventListener('pointerdown', onPanStart);
    renderer.domElement.addEventListener('pointermove', onPanMove);
    renderer.domElement.addEventListener('pointerup', onPanEnd);
    renderer.domElement.addEventListener('pointercancel', onPanEnd);

    const blockMenu = (event: Event) => event.preventDefault();
    renderer.domElement.addEventListener('contextmenu', blockMenu);

    sceneRef.current = { content, overlay, toWorld, panTo, zoomAt };

    let frame = 0;
    const tick = () => {
      frame = requestAnimationFrame(tick);

      // Fade the quarter-unit grid out when the cells get too small to aim at.
      // A 50-unit pad is 200 cells across, which at a distance is a grey haze
      // that hides the ship rather than helping you place it.
      const pixelsPerCell = (height / (baseSpan / view.zoom)) * GRID;
      fineMaterial.opacity = THREE.MathUtils.clamp((pixelsPerCell - 2.5) / 5, 0, 0.35);
      fineMaterial.visible = fineMaterial.opacity > 0.01;

      renderer.render(scene, camera);
    };
    tick();

    return () => {
      cancelAnimationFrame(frame);
      renderer.domElement.removeEventListener('wheel', onWheel);
      renderer.domElement.removeEventListener('pointerdown', onPanStart);
      renderer.domElement.removeEventListener('pointermove', onPanMove);
      renderer.domElement.removeEventListener('pointerup', onPanEnd);
      renderer.domElement.removeEventListener('pointercancel', onPanEnd);
      renderer.domElement.removeEventListener('contextmenu', blockMenu);
      viewport.dispose();
      sceneRef.current = null;
    };
  }, [berth]);

  // ── Drawing the design ─────────────────────────────────────────────────────
  useEffect(() => {
    const context = sceneRef.current;
    if (!context) return;

    const group = context.content;
    group.clear();

    const jointsById = new Map(joints.map((j) => [j.id, j]));
    const faulted = new Set(report.problems.flatMap((p) => [p.component.id, ...p.result.blockedBy]));
    const looseSet = new Set(loose);

    // ── The ghost ──────────────────────────────────────────────────────────
    //
    // What the template asks for and the ship has not got. Drawn first and
    // dimly, underneath everything real: it is a plan, and a plan that competes
    // with the thing you are building is in the way.
    if (template) {
      const templateJoints = new Map(template.graph.joints.map((j) => [j.id, j]));

      for (const line of missing.lines) {
        const a = templateJoints.get(line.a);
        const b = templateJoints.get(line.b);
        if (!a || !b) continue;

        const chosen = selected?.kind === 'ghostLine' && selected.id === line.id;
        group.add(
          asOverlay(
            new THREE.Line(
              new THREE.BufferGeometry().setFromPoints([
                new THREE.Vector3(a.x, a.y, 0),
                new THREE.Vector3(b.x, b.y, 0),
              ]),
              new THREE.LineDashedMaterial({
                color: chosen ? 0xffd479 : dimmed(0x5f7fa8, 0.5),
                dashSize: 0.35,
                gapSize: 0.3,
              }),
            ).computeLineDistances(),
            10,
          ),
        );
      }

      for (const component of missing.components) {
        const outline = componentOutline(component);
        const chosen = selected?.kind === 'ghostComponent' && selected.id === component.id;

        group.add(
          asOverlay(
            new THREE.LineLoop(
              new THREE.BufferGeometry().setFromPoints(
                outline.map((point) => new THREE.Vector3(point.x, point.y, 0)),
              ),
              new THREE.LineDashedMaterial({
                color: chosen ? 0xffd479 : dimmed(0x5f7fa8, 0.45),
                dashSize: 0.25,
                gapSize: 0.2,
              }),
            ).computeLineDistances(),
            11,
          ),
        );
      }
    }

    for (const compartment of compartments) {
      const points = compartment.jointIds
        .map((id) => jointsById.get(id))
        .filter((j): j is Joint => Boolean(j))
        .map((j) => new THREE.Vector2(j.x, j.y));
      if (points.length < 3) continue;

      /*
       * Opaque, and deliberately not blue.
       *
       * Translucent floors let the grid through, which put a lattice behind
       * every component and made the parts hard to pick out from the paper they
       * were drawn on. And the old blue was close enough to the component blues
       * to compete with them; a warm near-black reads as *inside the ship* and
       * leaves the whole cool end of the palette to mean something.
       */
      const mesh = new THREE.Mesh(
        new THREE.ShapeGeometry(new THREE.Shape(points)),
        new THREE.MeshBasicMaterial({ color: compartment.exposed ? 0x241d1a : 0x1a1512 }),
      );
      group.add(asOverlay(mesh, 6));
    }

    /*
     * Walls, as three stripes rather than one stroke.
     *
     * A wall carries three independent facts and they used to share one colour,
     * so paint and plating fought over the same pixel and integrity was a second
     * line drawn over the first. Reading any one of them meant knowing which was
     * winning. Three lanes side by side means each says its own thing and none
     * of them can be mistaken for another:
     *
     *   paint     — what you chose it to look like, and nothing else;
     *   plating   — what it is made of, in the grade's colour;
     *   integrity — how much of it is left, as a bar that empties.
     *
     * Drawn as quads rather than lines because line width is not something a
     * browser will honour: `linewidth` is ignored by every WebGL renderer, so a
     * thick line has to be a thin rectangle.
     */
    const LANE = GRID * 0.12;
    /** How far the selection rim stands out past the channels. */
    const OUTLINE = GRID * 0.05;

    /** A rectangle from `from` to `to`, `width` across, offset sideways. */
    const stripe = (
      from: Joint,
      to: Joint,
      width: number,
      offset: number,
      color: number,
      opacity = 1,
    ) => {
      const dx = to.x - from.x;
      const dy = to.y - from.y;
      const length = Math.hypot(dx, dy);
      if (length < 1e-6) return null;

      const nx = -dy / length;
      const ny = dx / length;

      const mesh = new THREE.Mesh(
        new THREE.PlaneGeometry(length, width),
        new THREE.MeshBasicMaterial({
          color,
          transparent: opacity < 1,
          opacity,
        }),
      );
      mesh.position.set(
        (from.x + to.x) / 2 + nx * offset,
        (from.y + to.y) / 2 + ny * offset,
        0,
      );
      mesh.rotation.z = Math.atan2(dy, dx);
      return mesh;
    };

    for (const line of lines) {
      const a = jointsById.get(line.a);
      const b = jointsById.get(line.b);
      if (!a || !b) continue;

      const chosen = selected?.kind === 'line' && selected.id === line.id;
      const health = healthOf(line.id);
      const left = Math.max(0, Math.min(1, health / LINE_HEALTH));
      const grade = gradeOfStrength(line.strength);

      /*
       * Every line here is on the ship.
       *
       * A wall that was destroyed is not in this graph at all — it was taken off
       * the hull when it failed — so there is no "breached" state to draw. What
       * it left behind shows as a template ghost, because the blueprint still
       * has it and the ship does not, which is exactly what a ghost means.
       */

      /*
       * Selection is an outline, not a colour.
       *
       * Painting the wall white to say "this one" overwrote the three things the
       * wall is there to tell you — and it did it to the wall you were about to
       * change the paint of, which is the worst possible moment to hide the
       * paint. A border says the same thing and takes nothing away.
       *
       * Drawn as a slightly wider stripe *behind* the channels, so what shows is
       * a rim on either side of them.
       */
      if (chosen) {
        const outline = stripe(a, b, LANE * 3 + OUTLINE * 2, 0, 0xffffff);
        if (outline) group.add(asOverlay(outline, 13));

        // Capped, or a selected wall is a rectangle with two bare ends and reads
        // as unfinished next to the rounded joints.
        for (const end of [a, b]) {
          const cap = new THREE.Mesh(
            new THREE.CircleGeometry((LANE * 3) / 2 + OUTLINE, 12),
            new THREE.MeshBasicMaterial({ color: 0xffffff }),
          );
          cap.position.set(end.x, end.y, 0);
          group.add(asOverlay(cap, 13));
        }
      }

      const paint = stripe(a, b, LANE, -LANE, new THREE.Color(line.color).getHex());
      if (paint) group.add(asOverlay(paint, 15));

      const plating = stripe(a, b, LANE, 0, new THREE.Color(grade.color).getHex());
      if (plating) group.add(asOverlay(plating, 15));

      // The integrity lane is a bar: a dark trough the full length of the wall,
      // with the surviving fraction filled from the wall's own start. Empty
      // trough on a wall about to go is the thing you want visible at a glance.
      const trough = stripe(a, b, LANE, LANE, 0x2a1c14);
      if (trough) group.add(asOverlay(trough, 15));

      if (left > 0) {
        const end = { id: '', x: a.x + (b.x - a.x) * left, y: a.y + (b.y - a.y) * left };
        const filled = stripe(
          a,
          end as Joint,
          LANE,
          LANE,
          left < 0.35 ? 0xff5f6b : left < 0.8 ? 0xffb347 : 0x7fb46f,
        );
        if (filled) {
          // Offset stays perpendicular to the *wall*, not to the shortened bar,
          // which is the same direction here because the bar is colinear.
          group.add(asOverlay(filled, 16));
        }
      }
    }

    /*
     * Joints, and the difference between a joint and the end of a wall.
     *
     * Every wall ends *at* a joint, so a plain dot at every end said nothing —
     * you could not tell a corner where three walls meet from a spur hanging off
     * one, and those are completely different things to a hull. A joint that
     * actually joins gets a ring; a bare end is left as a dot.
     *
     * Loose joints keep their amber, because each one is a joint you are paying
     * to keep and where the hull is still open is the thing you most want to see.
     */
    const walls = new Map<string, number>();
    for (const line of lines) {
      walls.set(line.a, (walls.get(line.a) ?? 0) + 1);
      walls.set(line.b, (walls.get(line.b) ?? 0) + 1);
    }

    for (const joint of joints) {
      const open = looseSet.has(joint.id);
      const joins = (walls.get(joint.id) ?? 0) > 1;

      const dot = new THREE.Mesh(
        new THREE.CircleGeometry(GRID * (open ? 0.42 : 0.26), open ? 16 : 10),
        new THREE.MeshBasicMaterial({ color: open ? 0xffd479 : 0xdbeaff }),
      );
      dot.position.set(joint.x, joint.y, 0);
      group.add(asOverlay(dot, 18));

      if (joins) {
        const ring = new THREE.Mesh(
          new THREE.RingGeometry(GRID * 0.46, GRID * 0.6, 18),
          new THREE.MeshBasicMaterial({ color: open ? 0xffd479 : 0xdbeaff }),
        );
        ring.position.set(joint.x, joint.y, 0);
        group.add(asOverlay(ring, 18));
      }
    }

    // The axis itself, so it is obvious what edits are being reflected about.
    if (mirror) {
      const reach = 1e4;
      // The centreline, running fore and aft.
      const ends: [number, number][] = [[0, -reach], [0, reach]];

      group.add(
        asOverlay(
          new THREE.Line(
            new THREE.BufferGeometry().setFromPoints(
              ends.map(([x, y]) => new THREE.Vector3(x, y, 0)),
            ),
            new THREE.LineDashedMaterial({
              color: dimmed(0xffd479, 0.5),
              dashSize: 0.5,
              gapSize: 0.4,
            }),
          ),
          12,
        ).computeLineDistances(),
      );
    }

    for (const component of components) {
      const bad = faulted.has(component.id);
      const chosen = selected?.kind === 'component' && selected.id === component.id;
      const outline = componentOutline(component);

      group.add(
        asOverlay(
          new THREE.LineLoop(
            new THREE.BufferGeometry().setFromPoints(
              outline.map((p) => new THREE.Vector3(p.x, p.y, 0)),
            ),
            new THREE.LineBasicMaterial({
              color: bad ? 0xff5f6b : chosen ? 0xffffff : 0x7fd4ff,
            }),
          ),
          chosen ? 22 : 20,
        ),
      );

      const spec = COMPONENT_SPECS[component.type];
      if (!spec.model) continue;

      void loadComponentGeometry(spec.model).then((geometry) => {
        if (!geometry || !sceneRef.current) return;
        const mesh = new THREE.Mesh(
          geometry,
          createStructureMaterial({
            color: bad ? 0xff5f6b : 0x9fc6e8,
            fadeDistance: 900,
            lightPosition: new THREE.Vector3(20, -30, 60),
          }),
        );
        // Solid, and depth-tested against itself.
        //
        // Everything else the editor draws is flat, so it can safely ignore
        // depth. A component is not flat, and switching depth testing off for
        // one drew its far side straight through its near side — every hidden
        // edge of the model showing at once, which is where the "edges render
        // over each other" came from. Lifted clear of the station instead, so
        // it can be depth-tested normally without the deck swallowing it.
        mesh.position.set(component.position.x, component.position.y, MESH_LIFT);
        // Not simply the facing — see `renderFacing`. A flip composed with a
        // rotation is not the rotation you started with.
        mesh.rotation.z = renderFacing(component);
        mesh.scale.setScalar(drawScaleOf(component.type));
        if (component.mirrored) mesh.scale.x *= -1;
        mesh.renderOrder = 17;
        group.add(mesh);
      });
    }
    // `condition` is in here because the damage marks are drawn from it — without
    // it a repair leaves the wall looking broken until something else forces a
    // redraw.
  }, [joints, lines, components, compartments, report, loose, mirror, selected, template, missing, condition]);

  // ── Interaction ────────────────────────────────────────────────────────────
  const drawingFrom = useRef<{ x: number; y: number } | null>(null);

  /**
   * Whether a line is half drawn, for anything that has to be *rendered*.
   *
   * The same fact as `drawingFrom`, in the form React can see. A ref does not
   * re-render, and the cancel button appears exactly when the ref becomes
   * non-null — so without this it would show up whenever something else happened
   * to redraw, which is worse than not having it.
   */
  const [drawing, setDrawing] = useState(false);

  const snap = useCallback(
    (point: { x: number; y: number }) => ({
      x: Math.round(point.x / GRID) * GRID,
      y: Math.round(point.y / GRID) * GRID,
    }),
    [],
  );

  const insidePad = useCallback(
    (point: { x: number; y: number }) =>
      !!berth && Math.abs(point.x) <= berth.half && Math.abs(point.y) <= berth.half,
    [berth],
  );

  const jointAt = useCallback(
    (at: { x: number; y: number }): Joint => {
      const existing = joints.find((j) => j.x === at.x && j.y === at.y);
      // Keyed by grid cell, so two lines drawn to the same point share a joint
      // and the compartment tracer sees them as genuinely connected.
      const cell = { x: Math.round(at.x / GRID), y: Math.round(at.y / GRID) };
      return existing ?? { id: `j${cell.x}_${cell.y}`, x: at.x, y: at.y };
    },
    [joints],
  );

  /** The ghost under the cursor: what would happen if you clicked here. */
  const preview = useCallback(
    (at: { x: number; y: number }) => {
      const context = sceneRef.current;
      if (!context) return;

      const group = context.overlay;
      group.clear();

      if (tool === 'place') {
        const candidate: ShipComponent = {
          id: '__preview',
          type: picked,
          position: snap(at),
          facing,
        };
        const twin = mirror ? mirrorComponent(candidate, '__preview-m') : null;

        // Three states, not two. Off the pad is refused outright; anything else
        // can be placed but amber says it still needs resolving before launch.
        const parts = [candidate, ...(twin ? [twin] : [])];
        const onPad = parts.every((part) => componentOutline(part).every((p) => insidePad(p)));
        const settled = parts.every((part) => checkPlacement(part, design, compartments).ok);
        const colour = !onPad ? 0xff5f6b : settled ? 0x7fb46f : 0xffd479;

        // The bounding box, which is the thing the rules actually test — so it
        // is the thing you should be able to see before committing to it.
        for (const part of [candidate, ...(twin ? [twin] : [])]) {
          const outline = componentOutline(part);
          group.add(
            asOverlay(
              new THREE.LineLoop(
                new THREE.BufferGeometry().setFromPoints(
                  outline.map((p) => new THREE.Vector3(p.x, p.y, 0)),
                ),
                new THREE.LineBasicMaterial({ color: colour }),
              ),
              31,
            ),
          );
          group.add(
            asOverlay(
              new THREE.Mesh(
                new THREE.ShapeGeometry(
                  new THREE.Shape(outline.map((p) => new THREE.Vector2(p.x, p.y))),
                ),
                new THREE.MeshBasicMaterial({ color: dimmed(colour, 0.35) }),
              ),
              30,
            ),
          );
        }
        return;
      }

      if (tool !== 'draw') return;

      const to = snap(at);
      const from = drawingFrom.current;

      // The point that would be used, so the snap is visible before clicking.
      const marker = new THREE.Mesh(
        new THREE.CircleGeometry(GRID * 0.4, 12),
        new THREE.MeshBasicMaterial({ color: dimmed(0x7fd4ff, 0.8) }),
      );
      marker.position.set(to.x, to.y, 0);
      group.add(asOverlay(marker, 31));

      if (mirror) {
        const reflected = mirrorPoint(to);
        const twinMarker = new THREE.Mesh(
          new THREE.CircleGeometry(GRID * 0.4, 12),
          new THREE.MeshBasicMaterial({ color: dimmed(0x7fd4ff, 0.45) }),
        );
        twinMarker.position.set(reflected.x, reflected.y, 0);
        group.add(asOverlay(twinMarker, 31));
      }

      if (!from) return;

      const length = Math.hypot(to.x - from.x, to.y - from.y);
      const cost = jointCostOf(design, from, to);
      const twin = mirror ? mirrorSegment(from, to) : null;

      /*
       * A piece for each wall, taken in turn.
       *
       * One `fitLine` against the whole hold would say yes to a mirrored pair
       * with a single line long enough in it — and the click would then refuse,
       * which is the ghost lying about the one thing it is drawn to answer.
       * `fitLine` picks the tightest piece that reaches, and the tightest piece
       * can only be spent once, so the second half has to ask what is left.
       */
      let stock: readonly StockLine[] = available;
      let affordable = true;

      for (let wall = 0; wall < (twin ? 2 : 1); wall++) {
        const fit = fitLine(stock, length);
        if (fit.kind === 'tooShort') {
          affordable = false;
          break;
        }
        if (fit.line) stock = stock.filter((line) => line.id !== fit.line!.id);
      }

      /*
       * Joints are still costed for one wall.
       *
       * Not an oversight: the twin's cost depends on the first half being down —
       * a pair meeting at the centreline shares the joint there — so doubling
       * this would refuse walls that are actually affordable. The click prices
       * them properly, against the ship as each half lands. This is the same
       * approximation it always was, and it errs towards letting you try.
       */
      const legal =
        insidePad(to) &&
        insidePad(from) &&
        (unlimited || affordable) &&
        (unlimited || report.jointsUsed + cost.net <= jointsOwned);

      for (const segment of [{ a: from, b: to }, ...(twin ? [twin] : [])]) {
        group.add(
          asOverlay(
            new THREE.Line(
              new THREE.BufferGeometry().setFromPoints([
                new THREE.Vector3(segment.a.x, segment.a.y, 0),
                new THREE.Vector3(segment.b.x, segment.b.y, 0),
              ]),
              new THREE.LineBasicMaterial({ color: legal ? 0x7fb46f : 0xff5f6b }),
            ),
            31,
          ),
        );
      }
    },
    [tool, picked, facing, mirror, design, compartments, insidePad, snap, available, unlimited, report, jointsOwned],
  );

  const onPointerMove = (event: React.PointerEvent) => {
    const context = sceneRef.current;
    if (!context) return;

    // Hover, which a finger does not have. The ghost is driven from the touch
    // handlers instead, off the press rather than off passing over.
    if (event.pointerType === 'touch') return;

    preview(context.toWorld(event.clientX, event.clientY));
  };

  /*
   * ── Fingers ────────────────────────────────────────────────────────────────
   *
   * One finger draws or drags, two move the view. Which of the two a single
   * finger turns out to be is decided by how far it travels, exactly as the
   * chart decides it: `button` is always 0 on touch, so there is no button to
   * read, and a gesture that has not moved yet is a tap that has not finished.
   *
   * Touch events rather than pointer events, because pinch needs two positions
   * at once and `TouchList` is where they already are together.
   */
  const gesture = useRef<{
    /** Where the first finger went down, and where the last move was seen. */
    start: Point;
    last: Point;
    /** Distance between two fingers, when there are two. */
    spread: number;
    /** How far this gesture has travelled, in total rather than net. */
    travelled: number;
    /** Two fingers have been down at some point, so no tap can come of it. */
    multi: boolean;
  } | null>(null);

  /**
   * How far a finger may wander and still count as a tap, in pixels.
   *
   * Generous, because a tap on glass is never still — a thumb rolls several
   * pixels on the way down. Small enough that a deliberate drag is never mistaken
   * for one.
   */
  const TAP_SLOP = 9;

  const midpoint = (touches: React.TouchList): Point => ({
    x: (touches[0]!.clientX + touches[1]!.clientX) / 2,
    y: (touches[0]!.clientY + touches[1]!.clientY) / 2,
  });

  const spreadOf = (touches: React.TouchList): number =>
    Math.hypot(
      touches[0]!.clientX - touches[1]!.clientX,
      touches[0]!.clientY - touches[1]!.clientY,
    );

  const onTouchStart = (event: React.TouchEvent) => {
    const touches = event.touches;

    if (touches.length === 1) {
      const at = { x: touches[0]!.clientX, y: touches[0]!.clientY };
      gesture.current = { start: at, last: at, spread: 0, travelled: 0, multi: false };

      // The ghost, under the finger, before anything is committed. This is the
      // only preview a touch device gets — there is no hover to give it earlier
      // — so it appears on the press and the lift is what acts on it.
      const context = sceneRef.current;
      if (context) preview(context.toWorld(at.x, at.y));
      return;
    }

    if (touches.length >= 2 && gesture.current) {
      gesture.current.multi = true;
      gesture.current.spread = spreadOf(touches);
      gesture.current.last = midpoint(touches);
    }
  };

  const onTouchMove = (event: React.TouchEvent) => {
    const context = sceneRef.current;
    const active = gesture.current;
    if (!context || !active) return;

    const touches = event.touches;

    if (touches.length >= 2) {
      const centre = midpoint(touches);
      const spread = spreadOf(touches);

      // Both at once, and in this order: the zoom is taken about the midpoint,
      // then the midpoint's own movement is the pan. Pinching and dragging are
      // one gesture on a touchscreen and separating them makes both feel stiff.
      if (active.spread > 0 && spread > 0) context.zoomAt(spread / active.spread, centre);
      context.panTo(active.last, centre);

      active.spread = spread;
      active.last = centre;
      active.travelled = Infinity;
      return;
    }

    const at = { x: touches[0]!.clientX, y: touches[0]!.clientY };
    active.travelled += Math.hypot(at.x - active.last.x, at.y - active.last.y);

    if (active.travelled <= TAP_SLOP) {
      active.last = at;
      return;
    }

    // Past the slop it is a drag, and a drag moves the view. The ghost goes:
    // it belongs to a tap that is no longer going to happen.
    context.overlay.clear();
    context.panTo(active.last, at);
    active.last = at;
  };

  const onTouchEnd = (event: React.TouchEvent) => {
    const active = gesture.current;

    // Not finished while a finger is still down — lifting one of two is the end
    // of the pinch, not the end of the gesture.
    if (event.touches.length > 0) {
      if (active) active.last = { x: event.touches[0]!.clientX, y: event.touches[0]!.clientY };
      return;
    }

    gesture.current = null;
    if (!active) return;

    const context = sceneRef.current;
    if (!context) return;

    if (active.multi || active.travelled > TAP_SLOP) {
      context.overlay.clear();
      return;
    }

    use(context.toWorld(active.start.x, active.start.y));
  };

  const onPointerDown = (event: React.PointerEvent) => {
    const context = sceneRef.current;
    // Middle and right belong to the camera; only a left click is a tool action.
    if (!context || event.button !== 0) return;

    // A finger's tool action waits for the lift, because until then this might
    // still turn out to be a pan. See `onTouchEnd`.
    if (event.pointerType === 'touch') return;

    use(context.toWorld(event.clientX, event.clientY));
  };

  /**
   * Do whatever the current tool does, at a point in the world.
   *
   * Split out from the pointer handler so a tap can reach it too — a mouse acts
   * on the press and a finger cannot, because a press that has not been lifted
   * yet is not distinguishable from the beginning of a drag.
   */
  const use = (at: { x: number; y: number }) => {
    const context = sceneRef.current;
    if (!context) return;

    if (tool === 'place') {
      // Snapped like everything else. The grid is fine enough that it costs no
      // freedom worth having, and it makes a symmetric pair land on exactly
      // reflected coordinates instead of a hair apart.
      const candidate: ShipComponent = {
        id: `c${Date.now()}`,
        type: picked,
        position: snap(at),
        facing,
      };
      const twin = mirror ? mirrorComponent(candidate, `${candidate.id}-m`) : null;
      const both = [candidate, ...(twin ? [twin] : [])];

      // The pad is the one hard limit: the berth is only so big, and a part
      // hanging over the edge could not be built here at all.
      for (const part of both) {
        if (!componentOutline(part).every((p) => insidePad(p))) {
          setHint('That would hang off the pad.');
          return;
        }
      }

      // Everything else is allowed and merely flagged. Dropping parts into open
      // space and drawing the hull around them afterwards is a normal way to
      // build; what is not allowed is *leaving* with the ship in that state.
      const faults = both.flatMap((part) => {
        const against = { ...design, components: [...components, ...both.filter((o) => o !== part)] };
        return checkPlacement(part, against, compartments).faults;
      });

      // Paid for out of the hold — one part per copy, so mirroring costs two.
      // Checked and spent together, or a mirrored pair with one part left in
      // cargo would fit half of itself.
      if (!spendParts(picked, both.length)) return;

      onChange({ ...design, components: [...components, ...both] });
      setHint(
        faults.length > 0
          ? `${COMPONENT_SPECS[picked].name} placed, but ${[...new Set(faults)].join(' and ')} — resolve before leaving.`
          : twin
            ? 'Placed, mirrored.'
            : '',
      );
      return;
    }

    if (tool === 'select') {
      // Ghost parts first, and only where there is nothing real. A plan is
      // something you point at *before* it exists, so once the real part is
      // there the ghost is gone and this never fires.
      const onGhostPart = missing.components.find((component) => {
        const outline = componentOutline(component);
        const xs = outline.map((p) => p.x);
        const ys = outline.map((p) => p.y);
        return (
          at.x >= Math.min(...xs) && at.x <= Math.max(...xs) &&
          at.y >= Math.min(...ys) && at.y <= Math.max(...ys)
        );
      });

      if (onGhostPart) {
        setSelected({ kind: 'ghostComponent', id: onGhostPart.id });
        setHint('');
        return;
      }

      // Components first: they sit on top of the lines that hold them, so a
      // click inside one is meant for the component, not the wall behind it.
      const onComponent = components.find((component) => {
        const outline = componentOutline(component);
        const xs = outline.map((p) => p.x);
        const ys = outline.map((p) => p.y);
        return (
          at.x >= Math.min(...xs) && at.x <= Math.max(...xs) &&
          at.y >= Math.min(...ys) && at.y <= Math.max(...ys)
        );
      });

      if (onComponent) {
        setSelected({ kind: 'component', id: onComponent.id });
        setHint('');
        return;
      }

      // Then the nearest line, rather than requiring a hit on a one-pixel
      // stroke — a line is a thing you point at, not a thing you have to catch.
      let best: string | null = null;
      let nearest = GRID * 3;

      for (const line of lines) {
        const a = joints.find((j) => j.id === line.a);
        const b = joints.find((j) => j.id === line.b);
        if (!a || !b) continue;

        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const lengthSquared = dx * dx + dy * dy;
        const t =
          lengthSquared < 1e-9
            ? 0
            : Math.max(0, Math.min(1, ((at.x - a.x) * dx + (at.y - a.y) * dy) / lengthSquared));
        const distance = Math.hypot(at.x - (a.x + t * dx), at.y - (a.y + t * dy));

        if (distance > nearest) continue;
        nearest = distance;
        best = line.id;
      }

      if (best) {
        setSelected({ kind: 'line', id: best });
        setHint('');
        return;
      }

      // Then the ghost walls, on the same nearest-within-reach rule.
      const templateJoints = new Map((template?.graph.joints ?? []).map((j) => [j.id, j]));
      let ghost: string | null = null;
      let ghostDistance = GRID * 3;

      for (const line of missing.lines) {
        const a = templateJoints.get(line.a);
        const b = templateJoints.get(line.b);
        if (!a || !b) continue;

        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const lengthSquared = dx * dx + dy * dy;
        const t =
          lengthSquared < 1e-9
            ? 0
            : Math.max(0, Math.min(1, ((at.x - a.x) * dx + (at.y - a.y) * dy) / lengthSquared));
        const distance = Math.hypot(at.x - (a.x + t * dx), at.y - (a.y + t * dy));

        if (distance > ghostDistance) continue;
        ghostDistance = distance;
        ghost = line.id;
      }

      setSelected(ghost ? { kind: 'ghostLine', id: ghost } : null);
      setHint(ghost ? '' : 'Nothing there.');
      return;
    }

    if (tool === 'erase') {
      const hit = components.find((component) => {
        const outline = componentOutline(component);
        const xs = outline.map((p) => p.x);
        const ys = outline.map((p) => p.y);
        return (
          at.x >= Math.min(...xs) && at.x <= Math.max(...xs) &&
          at.y >= Math.min(...ys) && at.y <= Math.max(...ys)
        );
      });
      if (hit) {
        // Back into the hold rather than gone. Removing a part you paid for and
        // having it evaporate would make every experiment cost money.
        if (cargo && onCargoChange && !unlimited) {
          const returned = stow(
            cargo,
            {
              id: `component-${hit.type}`,
              kind: 'component',
              label: COMPONENT_SPECS[hit.type].name,
              type: hit.type,
              count: 1,
            },
            capacity,
          );

          if (!returned) {
            setHint(`No room in cargo for the ${COMPONENT_SPECS[hit.type].name} you removed.`);
            return;
          }
          onCargoChange(returned);
        }

        onChange({ ...design, components: components.filter((c) => c.id !== hit.id) });
        return;
      }

      const grid = snap(at);
      const near = lines.find((line) => {
        const a = joints.find((j) => j.id === line.a);
        const b = joints.find((j) => j.id === line.b);
        return (a && a.x === grid.x && a.y === grid.y) || (b && b.x === grid.x && b.y === grid.y);
      });
      if (!near) return;

      const remaining = lines.filter((l) => l.id !== near.id);
      const used = new Set(remaining.flatMap((l) => [l.a, l.b]));
      onChange({
        ...design,
        lines: remaining,
        joints: joints.filter((j) => used.has(j.id)),
      });
      return;
    }

    // Drawing: first click sets the start, second completes the line.
    const grid = snap(at);
    if (!insidePad(grid)) {
      setHint('Outside the pad — that is as large as this berth allows.');
      return;
    }

    if (!drawingFrom.current) {
      drawingFrom.current = grid;
      setDrawing(true);
      setHint('Click again to finish the line.');
      return;
    }

    const from = drawingFrom.current;
    if (from.x === grid.x && from.y === grid.y) {
      drawingFrom.current = null;
      setDrawing(false);
      setHint('');
      return;
    }

    const length = Math.hypot(grid.x - from.x, grid.y - from.y);

    // Both halves are laid down in one edit, so undoing or validating sees a
    // symmetric ship rather than a momentarily lopsided one. `mirrorSegment`
    // answers null for a wall already lying on the centreline, which is its own
    // reflection and must not be built — or paid for — twice.
    const twin = mirror ? mirrorSegment(from, grid) : null;
    const segments = [{ a: from, b: grid }, ...(twin ? [twin] : [])];

    const nextJoints = [...joints];
    const nextLines = [...lines];
    const claim = (at: { x: number; y: number }): Joint => {
      const existing = nextJoints.find((j) => j.x === at.x && j.y === at.y);
      if (existing) return existing;
      const made = jointAt(at);
      nextJoints.push(made);
      return made;
    };

    /*
     * ── A mirrored pair is two walls, and costs like two ──────────────────────
     *
     * It used to cost one. A single `fitLine` was taken for the primary segment
     * and both halves were built from it, and the joints were costed once as
     * well — so the mirror was a button that halved the price of anything
     * symmetric. The place tool has always charged per copy; this is the draw
     * tool agreeing with it.
     *
     * Which means fitting each half against what is left after the previous one,
     * rather than doubling a single answer: `fitLine` picks the tightest piece
     * that reaches, and the tightest piece can only be spent once. A hold with
     * one line long enough can no longer build a mirrored pair, which is the
     * point.
     *
     * Joints are costed the same way, against the ship as it will stand once the
     * earlier half is down — a mirrored pair meeting at the centreline shares
     * the joint there, and costing both halves against the untouched design
     * would charge for it twice.
     *
     * Nothing is committed inside this loop that a refusal would have to undo:
     * `nextJoints` and `nextLines` are local copies, and the hold is not touched
     * until every half has been priced.
     */
    let stock: readonly StockLine[] = available;
    const fits: Fit[] = [];
    let spent = 0;
    let refunded = 0;

    for (const [index, segment] of segments.entries()) {
      const fit = fitLine(stock, length);

      if (!unlimited && fit.kind === 'tooShort') {
        setHint(
          segments.length > 1
            ? `Mirroring lays two walls of ${length.toFixed(2)} and cargo has line for ${index === 0 ? 'neither' : 'one'}.`
            : `No line in cargo reaches ${length.toFixed(2)}.`,
        );
        return;
      }

      const cost = jointCostOf(
        { ...design, joints: nextJoints, lines: nextLines },
        segment.a,
        segment.b,
      );

      spent += cost.spent;
      refunded += cost.refunded;

      if (!unlimited && spent > jointsHeld) {
        setHint(
          `That needs ${spent} joint${spent === 1 ? '' : 's'} and you hold ${jointsHeld}. Build from an existing point instead.`,
        );
        return;
      }

      // Off the shelf for the next half to choose from. Not out of the hold —
      // that happens once, below, when the whole thing is known to be possible.
      if (fit.line) stock = stock.filter((line) => line.id !== fit.line!.id);
      fits.push(fit);

      const a = claim(segment.a);
      const b = claim(segment.b);
      nextLines.push({
        id: `l${Date.now()}_${index}`,
        a: a.id,
        b: b.id,
        length,
        health: fit.line?.health ?? DEFAULT_LINE.health,
        strength: fit.line?.strength ?? DEFAULT_LINE.strength,
        color: fit.line?.color ?? DEFAULT_LINE.color,
      });
    }

    // Past every refusal, so the line being drawn is finished rather than
    // abandoned. Held until here on purpose: a wall that could not be afforded
    // used to clear the start point anyway, and the second click had to be
    // guessed at again from nothing.
    drawingFrom.current = null;
    setDrawing(false);

    // Out of the hold: a piece per wall, and the joints they need.
    if (cargo && onCargoChange && !unlimited) {
      let after: CargoItem[] = [...cargo];
      let melted = 0;

      for (const fit of fits) {
        if (!fit.line) continue;

        const stackId = fit.line.id.split('#')[0]!;
        after = take(after, stackId);

        /*
         * What is left of the piece, back in the hold.
         *
         * `fitLine` picks the tightest line that will reach, and the rest of it
         * was simply gone: a five-unit wall drawn with a ten in the hold cost
         * the whole ten. The shared model has always said otherwise — `Fit`
         * carries a `remainder` and `consumeLine` returns it — and this path
         * never used either, so the offcut existed in the type and nowhere else.
         *
         * Rounded *down* to a hundredth. Lengths come out of square roots of
         * grid offsets and are almost never round, and rounding up would mint
         * length out of floating point — a cut that gave back fractionally more
         * than it took, repeated, is a line printer.
         */
        const offcut = Math.floor(fit.remainder * 100) / 100;

        if (fit.kind === 'cut' && offcut >= MIN_LINE_LENGTH) {
          const returned = stow(
            after,
            {
              // Keyed by length, like every other line, so it stacks with any
              // other offcut of the same size rather than littering the hold
              // with one entry per cut.
              id: `line-${offcut}-${fit.line.strength}`,
              kind: 'line',
              label: `${offcut}u line`,
              length: offcut,
              strength: fit.line.strength,
              color: fit.line.color,
              count: 1,
            },
            capacity,
          );

          // It came out of a longer piece that has just left the hold, so it
          // fits by construction — but the hold is the authority on that, and a
          // refusal here means keeping the offcut rather than the wall.
          if (returned) after = returned;
        } else if (fit.kind === 'cut' && fit.remainder > 0) {
          /*
           * Too short to keep, so it is melted down rather than dropped.
           *
           * `LINELITS_PER_UNIT` is the reference rate the whole economy is
           * priced against, and a scrap of line is worth exactly its own length
           * in it — which is why a trader sells at a markup over the same
           * number, and why melting what you buy is not a money press.
           *
           * Summed across the halves and paid once at the end: two calls to
           * `onWalletChange` in a row would both read the same `wallet` from
           * this render, and the second would overwrite the first.
           */
          melted += fit.remainder * LINELITS_PER_UNIT;
        }
      }

      if (melted > 0 && wallet && onWalletChange) {
        // Carried rather than banked: it never left the ship. And fractional,
        // which the wallet expects — its own comparison slack exists because
        // money summed from offcuts does not land on whole numbers.
        onWalletChange({ ...wallet, carried: wallet.carried + melted });
      }

      for (let i = 0; i < spent; i++) {
        const next = takeJoint(after);
        if (next) after = next;
      }

      onCargoChange(after);
    }

    onChange({ ...design, joints: nextJoints, lines: nextLines });

    const jointNote =
      spent > 0
        ? ` ${spent} joint${spent === 1 ? '' : 's'} used.`
        : refunded > 0
          ? ` ${refunded} joint${refunded === 1 ? '' : 's'} refunded.`
          : ' No joint needed.';

    // The first half speaks for both: they are the same length, so the two cuts
    // differ only in which piece happened to be tightest, and a hint naming both
    // would be twice as long to say the same thing.
    const first = fits[0];

    setHint(
      (unlimited || !first?.line
        ? `Line ${length.toFixed(2)} placed${twin ? ', mirrored' : ''}.`
        : `${twin ? 'Two walls' : 'One wall'} of ${length.toFixed(2)}: ${
            first.kind === 'exact'
              ? 'an exact fit'
              : `cut from a ${first.line.length.toFixed(2)}, ${first.remainder.toFixed(2)} ${
                  first.remainderBecomesLinelits ? 'to linelits' : 'back to cargo'
                }`
          }.`) + jointNote,
    );
  };

  /**
   * Designs are portable text.
   *
   * The same file serves three jobs: authoring the AI ships that ship with the
   * game, letting players trade hulls, and keeping your own work between
   * sessions. Reading is validated in `@interspacial/shared` — a design from
   * someone else is untrusted input.
   */
  /**
   * Put a planned part on the ship.
   *
   * Reuses the template's own ids, which is what lets `missing` tick the item
   * off — and what makes rebuilding a hull put back *that* wall rather than a
   * new one that happens to be in the same place.
   *
   * Line comes out of cargo the same way drawing it would, so a template is not
   * a way round paying for the ship. Components do not, because there is no
   * component cargo yet; the trader charges for them and then has nowhere to
   * put them, and that hole is the same hole.
   */
  const buildPlanned = useCallback(
    (wanted: Line | ShipComponent) => {
      if (!template) return;

      if ('type' in wanted) {
        if (!spendParts(wanted.type)) return;

        onChange({ ...design, components: [...components, { ...wanted }] });
        setSelected({ kind: 'component', id: wanted.id });
        setHint(`Fitted ${COMPONENT_SPECS[wanted.type].name}.`);
        return;
      }

      const fit = fitLine(available, wanted.length);
      if (!unlimited && fit.kind === 'tooShort') {
        setHint(`No line in cargo reaches ${wanted.length.toFixed(2)}.`);
        return;
      }

      // The joints the wall needs, brought over from the template if the ship
      // has not got them. A line with nothing to end it is not a line.
      const nextJoints = [...joints];
      for (const id of [wanted.a, wanted.b]) {
        if (nextJoints.some((joint) => joint.id === id)) continue;
        const fromTemplate = template.graph.joints.find((joint) => joint.id === id);
        if (fromTemplate) nextJoints.push({ ...fromTemplate });
      }

      onChange({
        ...design,
        joints: nextJoints,
        lines: [
          ...lines,
          {
            ...wanted,
            health: fit.line?.health ?? DEFAULT_LINE.health,
            strength: fit.line?.strength ?? DEFAULT_LINE.strength,
            color: fit.line?.color ?? DEFAULT_LINE.color,
          },
        ],
      });

      setSelected({ kind: 'line', id: wanted.id });
      setHint(
        fit.kind === 'cut'
          ? `Built a ${wanted.length.toFixed(2)}u wall, cut from ${fit.line?.length.toFixed(2)}u.`
          : `Built a ${wanted.length.toFixed(2)}u wall.`,
      );
    },
    [template, design, components, joints, lines, available, unlimited, onChange],
  );

  /**
   * Take a part out of the hold to fit it.
   *
   * Returns false when there is none and the editor is not in debug, which is
   * the caller's cue to stop — a part fitted from nothing would let the hold be
   * bypassed entirely, and the hold is the only thing making parts cost
   * anything.
   */
  const spendParts = useCallback(
    (type: ComponentType, count = 1): boolean => {
      if (unlimited || !cargo || !onCargoChange) return true;

      let after: CargoItem[] = [...cargo];
      for (let i = 0; i < count; i++) {
        const next = takeComponent(after, type);
        if (!next) {
          const name = COMPONENT_SPECS[type].name;
          setHint(
            count > 1
              ? `Needs ${count} × ${name} and cargo has ${heldComponents(cargo)[type] ?? 0}.`
              : `No ${name} in cargo. Buy one from the trader.`,
          );
          return false;
        }
        after = next;
      }

      onCargoChange(after);
      return true;
    },
    [unlimited, cargo, onCargoChange],
  );

  const saveTemplate = useCallback(() => {
    const name = window.prompt('Name this template', 'Untitled') ?? 'Untitled';
    const blob = new Blob([exportDesign(name, design)], { type: 'application/json' });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${name.replace(/[^\w-]+/g, '-').toLowerCase()}.ship.json`;
    link.click();
    URL.revokeObjectURL(link.href);

    setHint(`Saved ${name}. Share the file — anyone can load it as a template.`);
  }, [design]);

  const loadTemplate = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,application/json';

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      const result = importDesign(await file.text());
      if (!result.ok) {
        setHint(`Could not read that template: ${result.error}`);
        return;
      }

      // Loaded as a *ghost*, not as the ship. Replacing the hull outright would
      // throw away whatever you were flying to look at somebody else's design,
      // and the whole point is to build towards one deliberately.
      setTemplate({ name: result.design.name, graph: result.design.graph });
      setTool('select');
      setHint(`${result.design.name} loaded as a template. Click a ghost part to build it.`);
    };

    input.click();
  }, []);

  /**
   * Abandon a wall that is half drawn.
   *
   * A function rather than a line inside the key handler, because escape is not
   * available to everybody: the draw tool is two taps, one to start the line and
   * one to finish it, and a device with no keyboard had no way to say "not that
   * one after all" — the only exit was to draw a wall you did not want and then
   * erase it.
   */
  const cancelDraw = useCallback(() => {
    drawingFrom.current = null;
    setDrawing(false);
    sceneRef.current?.overlay.clear();
    setHint('');
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'r') setFacing((f) => (f + 1) % 8);
      if (event.key === 'Escape') cancelDraw();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cancelDraw]);

  const { performance } = report;
  const jointsLeft = jointsOwned - report.jointsUsed;

  return (
    <>
      <div
        ref={mountRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        style={{
          position: 'absolute',
          inset: 0,
          cursor: 'crosshair',
          /*
           * `none`, so the browser never takes a gesture that starts here.
           *
           * It has to be the CSS rather than `preventDefault`, because React
           * registers `touchmove` as a passive listener and a passive listener
           * cannot prevent anything. Without it a drag across the pad scrolls
           * the page and a pinch zooms the whole game rather than the ship.
           */
          touchAction: 'none',
        }}
      />

      {/*
        The bar stays the width of the screen; its contents shrink inside it.

        Scaling the bar itself would take its background with it and leave the
        gradient covering two thirds of the top of the screen, which reads as a
        panel that failed to load. The two groups are pinned to opposite corners
        and scale about those, so each stays against the edge it belongs to.
      */}
      <div style={{ ...bar, top: 0, padding: TOUCH_UI ? 6 : 10 }}>
        <div style={{ display: 'flex', ...hudScaled('top left') }}>
          {(['draw', 'place', 'select', 'erase'] as Tool[]).map((id) => (
            <button key={id} type="button" onClick={() => setTool(id)} style={tab(tool === id)}>
              {id}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', ...hudScaled('top right') }}>
          <button
            type="button"
            onClick={() => setMirror(!mirror)}
            style={tab(mirror)}
            title="Mirror every edit across the ship's centreline"
          >
            {'│'} mirror
          </button>

          {/* A debugging tool, and shown only to somebody who is debugging.
              Offered to everyone it made the economy optional: line, joints and
              parts are what cargo is *for*. */}
          {admin && (
            <label style={{ fontSize: 11, opacity: 0.65, display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="checkbox" checked={unlimited} onChange={(e) => setUnlimited(e.target.checked)} />
              unlimited parts
            </label>
          )}
          {/* Damage, and the way out of it.
              Sits with the tools rather than in a panel you have to open,
              because a wall at nine points is something you want to trip over
              on the way in, not discover on approach. */}
          {damaged.length > 0 && (
            <>
              <button
                type="button"
                onClick={() => {
                  if (!act) return;
                  void act('/repair').then((ok) => {
                    if (ok) setHint(`Hull restored for ${formatLinelits(repairBill)}L.`);
                  });
                }}
                disabled={!wallet || !canAfford(wallet, repairBill)}
                style={{
                  ...action,
                  borderColor: 'rgba(255,143,107,0.6)',
                  color: '#ff8f6b',
                  opacity: wallet && canAfford(wallet, repairBill) ? 1 : 0.5,
                }}
                title={`${damaged.length} line${damaged.length === 1 ? '' : 's'} damaged`}
              >
                ⚠ repair {damaged.length} · {formatLinelits(repairBill)}L
              </button>

              {/* What you can afford, when you cannot afford all of it.
                  A ship that limps in with less than the full price should not
                  be told the only option costs everything — each of these buys
                  that share of the damage across every wall, at the same rate
                  per point as the whole. */}
              {!(wallet && canAfford(wallet, repairBill)) && (
                <div style={{ display: 'flex', gap: 4 }}>
                  {REPAIR_STEPS.filter((step) => step < 1).map((step) => {
                    const bill = repairBill * step;
                    const affordable = !!wallet && canAfford(wallet, bill);

                    return (
                      <button
                        key={step}
                        type="button"
                        onClick={() => {
                          if (!act) return;
                          void act('/repair', { fraction: step }).then((ok) => {
                            if (ok) setHint(`Hull patched for ${formatLinelits(bill)}L.`);
                          });
                        }}
                        disabled={!affordable || !act}
                        style={{
                          ...action,
                          flex: 1,
                          padding: '5px 2px',
                          fontSize: 10,
                          borderColor: 'rgba(255,143,107,0.45)',
                          color: '#ff8f6b',
                          opacity: affordable ? 1 : 0.4,
                        }}
                        title={`Buy back ${Math.round(step * 100)}% of the damage for ${formatLinelits(bill)}L`}
                      >
                        {Math.round(step * 100)}%
                        <br />
                        {formatLinelits(bill)}L
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}
          <button
            type="button"
            onClick={saveTemplate}
            style={action}
            title="Download this design as a template file you can share"
          >
            save template
          </button>
          <button
            type="button"
            onClick={loadTemplate}
            style={action}
            title="Show another design as a ghost to build towards"
          >
            load template
          </button>
          {template && (
            <button
              type="button"
              onClick={() => {
                setTemplate(null);
                setHint('Template cleared.');
              }}
              style={{
                ...action,
                borderColor:
                  missing.lines.length + missing.components.length > 0
                    ? 'rgba(255,212,121,0.5)'
                    : 'rgba(127,212,255,0.22)',
              }}
              title={`Showing ${template.name}`}
            >
              ghost: {missing.lines.length + missing.components.length} left
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              if (report.problems.length > 0) {
                setHint(
                  `${report.problems.length} component${report.problems.length === 1 ? '' : 's'} still misplaced — build the hull around them, or remove them.`,
                );
                return;
              }
              onExit();
            }}
            style={{
              ...action,
              opacity: report.problems.length > 0 ? 0.45 : 1,
              borderColor: report.problems.length > 0 ? 'rgba(255,212,121,0.4)' : 'rgba(127,212,255,0.22)',
            }}
            title={report.problems.length > 0 ? 'Resolve misplaced components first' : 'Leave the editor'}
          >
            done
          </button>
        </div>
      </div>

      {/* What there is to build with.
          The place tool has always listed the parts on hand; drawing had
          nothing, so the only way to find out whether you were carrying a
          six-unit line was to try to draw one and watch it fail. */}
      {tool === 'draw' && (
        <div
          style={{
            ...panel,
            ...hudScaled('top left'),
            top: PANEL_TOP,
            left: 10,
            width: 215,
            maxHeight: '70vh',
            overflowY: 'auto',
          }}
        >
          <div style={label}>line in the hold</div>

          {/* Only while there is something to abandon. A permanent cancel button
              is a button that does nothing most of the time, and this one is
              here for the case where there is no escape key to press. */}
          {drawing && (
            <button
              type="button"
              onClick={cancelDraw}
              style={{ ...action, width: '100%', marginBottom: 6 }}
            >
              cancel this line{SHOW_KEY_HINTS ? ' (esc)' : ''}
            </button>
          )}

          {available.length === 0 && (
            <div style={{ opacity: 0.45, fontSize: 11, marginTop: 4 }}>
              None. Buy line from a trader, or lay some at a fabricator.
            </div>
          )}

          {/* Grouped by length and grade, which is what a stack is — `available`
              is expanded into individual pieces because the fitter picks the
              tightest one that will do, and it cannot pick against a count. */}
          {[...available
            .reduce((stacks, piece) => {
              const key = `${piece.length}|${piece.strength}`;
              const stack = stacks.get(key);
              if (stack) stack.count += 1;
              else stacks.set(key, { length: piece.length, strength: piece.strength, count: 1 });
              return stacks;
            }, new Map<string, { length: number; strength: number; count: number }>())
            .values()]
            .sort((a, b) => a.length - b.length || a.strength - b.strength)
            .map((stack) => {
              const grade = gradeOfStrength(stack.strength);

              return (
                <div
                  key={`${stack.length}|${stack.strength}`}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, fontSize: 11.5 }}
                >
                  <span
                    style={{
                      width: 3,
                      alignSelf: 'stretch',
                      background: grade.color,
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ flex: 1 }}>{stack.length}u</span>
                  <span style={{ opacity: 0.42, fontSize: 10 }}>{grade.name}</span>
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>×{stack.count}</span>
                </div>
              );
            })}

          <div style={{ ...label, marginTop: 10 }}>joints</div>
          <div style={{ display: 'flex', gap: 6, fontSize: 11.5, marginTop: 3 }}>
            <span style={{ flex: 1, opacity: 0.6 }}>
              {jointsHeld === Infinity ? 'unlimited' : 'in the hold'}
            </span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              {jointsHeld === Infinity ? '∞' : `×${jointsHeld}`}
            </span>
          </div>
          <div style={{ opacity: 0.4, fontSize: 10, lineHeight: 1.4, marginTop: 3 }}>
            Every open end holds one. Closing a shape gives them back.
          </div>
        </div>
      )}

      {tool === 'place' && (
        <div style={{ ...panel, ...hudScaled('top left'), top: PANEL_TOP, left: 10, width: 215 }}>
          <div style={label}>component</div>
          {COMPONENT_TYPES.map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => setPicked(type)}
              style={{
                ...action,
                display: 'block',
                width: '100%',
                textAlign: 'left',
                marginTop: 3,
                background: picked === type ? 'rgba(127,212,255,0.18)' : 'transparent',
                border: 'none',
              }}
            >
              <div>{COMPONENT_SPECS[type].name}</div>
              <div style={{ opacity: 0.42, fontSize: 10, lineHeight: 1.4 }}>
                {statsOf(type).join(' · ')}
              </div>
            </button>
          ))}
          {/*
            The facing, as a control rather than an instruction.

            It was `press R`, and `r` is still the shortcut — but a facing is not
            a detail: a thruster pointed the wrong way is a ship that flies
            sideways, and on a device with no keyboard there was no way to change
            it at all. The reading stays, because knowing the angle matters as
            much as being able to turn it.
          */}
          <div
            style={{
              marginTop: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 11,
              opacity: 0.75,
            }}
          >
            <button
              type="button"
              onClick={() => setFacing((f) => (f + 7) % 8)}
              title="Turn the part anticlockwise"
              style={{ ...action, padding: '3px 9px' }}
            >
              ↺
            </button>
            <span style={{ minWidth: 42, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
              {facing * 45}°
            </span>
            <button
              type="button"
              onClick={() => setFacing((f) => (f + 1) % 8)}
              title="Turn the part clockwise (R)"
              style={{ ...action, padding: '3px 9px' }}
            >
              ↻
            </button>
          </div>
        </div>
      )}

      <div style={{ ...panel, ...hudScaled('top right'), top: PANEL_TOP, right: 10, width: 235 }}>
        <div style={label}>ship</div>
        <Row name="compartments" value={String(compartments.length)} />
        <Row name="surface area" value={report.area.toFixed(2)} />
        <Row name="lines" value={String(lines.length)} />
        <Row
          name="joints"
          value={`${report.jointsUsed} / ${jointsOwned}`}
          bad={!unlimited && jointsLeft < 0}
        />
        <Row
          name="power"
          value={`${performance.power.produced} / ${performance.power.demanded}`}
          bad={performance.power.satisfaction < 1}
        />
        <Row
          name="energy"
          value={`${performance.energy.produced} / ${performance.energy.demanded}`}
          bad={performance.energy.satisfaction < 1}
        />
        <Row name="max speed" value={performance.maxSpeed.toFixed(1)} />
        <Row name="cargo" value={`${cargoCapacity(components)} w`} />

        <div
          style={{
            marginTop: 8,
            paddingTop: 8,
            borderTop: '1px solid rgba(127,212,255,0.18)',
            color: performance.flyable && report.problems.length === 0 ? '#7fb46f' : '#ffd479',
          }}
        >
          {performance.flyable && report.problems.length === 0
            ? 'flightworthy'
            : !performance.flyable
              ? 'needs a pilot seat, engine and thruster'
              : `${report.problems.length} misplaced component${report.problems.length === 1 ? '' : 's'}`}
        </div>

        {loose.length > 0 && (
          <div style={{ marginTop: 6, fontSize: 11, color: '#ffd479' }}>
            {loose.length} open end{loose.length === 1 ? '' : 's'} — each holds a joint
          </div>
        )}
      </div>

      {tool === 'select' && (
        <div
          style={{
            ...panel,
            ...hudScaled('top left'),
            top: PANEL_TOP,
            left: 10,
            width: 232,
            maxHeight: '70vh',
            overflowY: 'auto',
          }}
        >
          <div style={label}>
            {selected?.kind === 'ghostLine' || selected?.kind === 'ghostComponent'
              ? 'planned'
              : selected?.kind === 'component'
                ? 'component'
                : 'line'}
          </div>

          {/* A planned part: something the template wants and the ship has not
              got. Building it here is the whole of the rebuild story — a hull
              that lost a wall, and a design a friend sent you, are the same
              problem with the same button. */}
          {(() => {
            if (selected?.kind === 'ghostLine') {
              const wanted = missing.lines.find((line) => line.id === selected.id);
              if (!wanted) return null;

              const fit = fitLine(available, wanted.length);
              return (
                <>
                  <Row name="length" value={wanted.length.toFixed(2)} />
                  <Row name="from" value={template?.name ?? 'template'} />
                  <Row
                    name="in cargo"
                    value={fit.kind === 'tooShort' ? 'nothing long enough' : `${fit.line?.length.toFixed(2)}u`}
                    warn={fit.kind === 'tooShort'}
                  />

                  <button
                    type="button"
                    onClick={() => buildPlanned(wanted)}
                    disabled={fit.kind === 'tooShort' && !unlimited}
                    style={{
                      ...action,
                      width: '100%',
                      marginTop: 10,
                      opacity: fit.kind === 'tooShort' && !unlimited ? 0.45 : 1,
                    }}
                  >
                    build from cargo
                  </button>
                </>
              );
            }

            if (selected?.kind === 'ghostComponent') {
              const wanted = missing.components.find((part) => part.id === selected.id);
              if (!wanted) return null;

              return (
                <>
                  <Row name="type" value={COMPONENT_SPECS[wanted.type].name} />
                  <Row name="from" value={template?.name ?? 'template'} />

                  <button
                    type="button"
                    onClick={() => buildPlanned(wanted)}
                    style={{ ...action, width: '100%', marginTop: 10 }}
                  >
                    fit from cargo
                  </button>
                </>
              );
            }

            return null;
          })()}

          {(() => {
            if (selected?.kind === 'ghostLine' || selected?.kind === 'ghostComponent') return null;

            // A component inspector, when that is what is selected.
            const chosen =
              selected?.kind === 'component'
                ? components.find((c) => c.id === selected.id)
                : undefined;

            if (chosen) {
              const spec = COMPONENT_SPECS[chosen.type];
              const placed = checkPlacement(chosen, design, compartments);

              return (
                <>
                  <div style={{ fontSize: 15, marginBottom: 2 }}>{spec.name}</div>
                  <div style={{ fontSize: 11, opacity: 0.5, lineHeight: 1.45, marginBottom: 8 }}>
                    {spec.description}
                  </div>

                  {statsOf(chosen.type).map((stat) => (
                    <div key={stat} style={{ fontSize: 12, opacity: 0.75 }}>
                      {stat}
                    </div>
                  ))}

                  <div style={{ marginTop: 8 }}>
                    <Row name="facing" value={`${(chosen.facing ?? 0) * 45}°`} />
                    <Row name="mounting" value={spec.mounting} />
                    {spec.mounting === 'edge' && (
                      <Row
                        name="status"
                        value={placed.active ? 'active' : 'dormant'}
                        bad={!placed.active}
                      />
                    )}
                  </div>

                  {spec.mounting === 'edge' && !placed.active && (
                    <div style={{ marginTop: 6, fontSize: 11, color: '#ffd479', lineHeight: 1.45 }}>
                      Mounted on an internal wall. It will wake if damage opens
                      this compartment to space.
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => {
                      onChange({ ...design, components: components.filter((c) => c.id !== chosen.id) });
                      setSelected(null);
                    }}
                    style={{ ...action, width: '100%', marginTop: 10 }}
                  >
                    remove
                  </button>
                </>
              );
            }

            const line = selected?.kind === 'line' ? lines.find((l) => l.id === selected.id) : undefined;
            if (!line) {
              return <div style={{ opacity: 0.5, fontSize: 12 }}>Click a line or a component.</div>;
            }

            const current = gradeOfStrength(line.strength);

            /** Spend one from the shelf and apply it. */
            /*
             * Plating is charged by the metre and by the tier.
             *
             * Both, because both are real: the same job on a span twice as long
             * is twice the work, and sintered is fifteen times the strength of
             * standard. Free plating made the whole grade ladder a formality —
             * you fitted the best the station had to every wall and never
             * thought about it again.
             */
            const applyGrade = (id: string) => {
              if (!act) return;
              const bill = platingCost(id, line.length);

              void act('/outfit', { lineId: line.id, grade: id }).then((ok) => {
                if (ok && bill > 0) setHint(`Plated for ${formatLinelits(bill)}L.`);
              });
            };

            // Paint is one pool, not a colour each: a station has paint or it
            // does not, and while it has some you may use any colour. Flat-rated
            // for the same reason — pricing it by the metre would tax a long
            // hull for having a bigger canvas, and paint changes nothing.
            const applyPaint = (color: string) => {
              if (!act) return;
              void act('/outfit', { lineId: line.id, color });
            };

            return (
              <>
                <Row name="length" value={line.length.toFixed(2)} />
                <Row name="weight" value={lineWeight(line.length).toFixed(2)} />
                {/* From the hull, not the blueprint — the blueprint's lines are
                    always whole, which would make the readout a constant. */}
                <Row
                  name="health"
                  value={`${Math.round(healthOf(line.id))} / ${LINE_HEALTH}`}
                  warn={healthOf(line.id) < LINE_HEALTH}
                />

                {/* Only when there is something to put back. A repair button on
                    an undamaged wall is a button that does nothing, and this
                    panel already has enough that does. */}
{/* As much of the wall as you can pay for.
                    One all-or-nothing button meant a ship that limped in with
                    less than the full price could not buy the part of the
                    repair it could afford. Each step buys that fraction of the
                    damage for that fraction of the bill, so buying in tenths
                    and buying once come to the same money. */}
                {healthOf(line.id) < LINE_HEALTH && (
                  <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
                    {REPAIR_STEPS.map((step) => {
                      const bill = lineRepairCost(
                        { health: healthOf(line.id), length: line.length },
                        step,
                      );
                      const affordable = !!wallet && canAfford(wallet, bill);

                      return (
                        <button
                          key={step}
                          type="button"
                          onClick={() => {
                            if (!act) return;
                            void act('/repair', { lineId: line.id, fraction: step }).then((ok) => {
                              if (ok) setHint(`Wall repaired for ${formatLinelits(bill)}L.`);
                            });
                          }}
                          disabled={!affordable || !act}
                          style={{
                            ...action,
                            flex: 1,
                            padding: '5px 2px',
                            fontSize: 10,
                            borderColor: 'rgba(255,143,107,0.6)',
                            color: '#ff8f6b',
                            opacity: affordable ? 1 : 0.4,
                          }}
                          title={
                            step === 1
                              ? `Put this wall back to ${LINE_HEALTH} for ${formatLinelits(bill)}L`
                              : `Buy back ${Math.round(step * 100)}% of the damage for ${formatLinelits(bill)}L`
                          }
                        >
                          {step === 1 ? 'full' : `${Math.round(step * 100)}%`}
                          <br />
                          {formatLinelits(bill)}L
                        </button>
                      );
                    })}
                  </div>
                )}

                <Row name="strength" value={`${line.strength} · ${current.name}`} />

                <div style={{ ...label, marginTop: 10 }}>plating</div>
                {LINE_GRADES.map((grade) => {
                  const held = shelf.grades[grade.id] ?? 0;
                  const bill = platingCost(grade.id, line.length);
                  // Priced by the metre, so the bill is this wall's bill — the
                  // same grade costs differently on a spur and on a long span,
                  // and saying so here is the only place it can be seen.
                  const broke = bill > 0 && !(wallet && canAfford(wallet, bill));
                  const out = held <= 0 || broke;

                  return (
                    <button
                      key={grade.id}
                      type="button"
                      disabled={out}
                      onClick={() => applyGrade(grade.id)}
                      style={shelfRow(current.id === grade.id, out)}
                      title={broke ? 'Not enough on the ship' : undefined}
                    >
                      <span style={{ color: out ? 'rgba(219,234,255,0.35)' : grade.color }}>
                        {grade.name}
                      </span>
                      <span style={{ opacity: 0.45, fontSize: 10 }}>
                        {bill > 0 ? `${formatLinelits(bill)}L` : 'free'} · {grade.strength} str
                        {held <= 0 ? ' · none' : Number.isFinite(held) ? ` · ${held} left` : ''}
                      </span>
                    </button>
                  );
                })}

                <div
                  style={{
                    ...label,
                    marginTop: 10,
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <span>paint · {formatLinelits(PAINT_COST)}L</span>
                  <span style={{ opacity: 0.8 }}>
                    {shelf.paint > 0 ? `${shelf.paint} left` : 'out of stock'}
                  </span>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 4 }}>
                  {PAINTS.map((paint) => (
                    <button
                      key={paint.id}
                      type="button"
                      disabled={shelf.paint <= 0}
                      title={paint.name}
                      onClick={() => applyPaint(paint.color)}
                      style={{
                        width: 22,
                        height: 22,
                        background: paint.color,
                        opacity: shelf.paint <= 0 ? 0.25 : 1,
                        border:
                          line.color === paint.color
                            ? '2px solid #dbeaff'
                            : '1px solid rgba(0,0,0,0.4)',
                        cursor: shelf.paint <= 0 ? 'not-allowed' : 'pointer',
                        padding: 0,
                      }}
                    />
                  ))}
                </div>

                <div style={{ marginTop: 8, fontSize: 10, opacity: 0.4, lineHeight: 1.5 }}>
                  Stock is this station's alone. Better plating is found further out.
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* Bottom left, clear of the mode switcher in the opposite corner. */}
      <div
        style={{
          position: 'absolute',
          bottom: 16,
          left: 16,
          display: 'grid',
          gap: 8,
          // 460 is wider than a phone held sideways has to spare, and this is
          // the one panel that is prose rather than controls.
          maxWidth: 460,
          ...hudScaled('bottom left'),
        }}
      >
        {hint && <div style={{ ...panel, position: 'relative' }}>{hint}</div>}

        {berth && (
          <div style={{ ...panel, position: 'relative', fontSize: 11, opacity: 0.75 }}>
            <span style={{ color: '#ffd479' }}>↑ nose points up</span> — build forward-facing.
            <br />
            {berth.station.name} · {berth.pad.padId} · build volume{' '}
            {(berth.half * 2).toFixed(1)} units square
          </div>
        )}
      </div>
    </>
  );
}

/**
 * The numbers that decide whether you want this part.
 *
 * Short enough to sit under a palette entry, so the choice can be made without
 * clicking through to a detail panel first.
 */
function statsOf(type: ComponentType): string[] {
  const spec = COMPONENT_SPECS[type];
  const bounds = boundsOf(type);
  const out: string[] = [
    `${(bounds.x.max - bounds.x.min).toFixed(1)}×${(bounds.y.max - bounds.y.min).toFixed(1)}`,
  ];

  if (spec.produces.power) out.push(`+${spec.produces.power} power`);
  if (spec.consumes.power) out.push(`−${spec.consumes.power} power`);
  if (spec.produces.energy) out.push(`+${spec.produces.energy} energy`);
  if (spec.consumes.energy) out.push(`−${spec.consumes.energy} energy`);
  if (spec.cargoCapacity) out.push(`${spec.cargoCapacity} cargo`);
  if (spec.mounting === 'edge') out.push('on an edge');

  return out;
}

function Row({
  name,
  value,
  bad,
  warn,
}: {
  name: string;
  value: string;
  bad?: boolean;
  warn?: boolean;
}) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
      <span style={{ opacity: 0.55 }}>{name}</span>
      <span style={{ color: bad ? '#ffd479' : warn ? '#ff8f6b' : '#dbeaff' }}>{value}</span>
    </div>
  );
}

const bar: React.CSSProperties = {
  position: 'absolute',
  left: 0,
  right: 0,
  display: 'flex',
  justifyContent: 'space-between',
  padding: 10,
  background: 'linear-gradient(rgba(6,10,18,0.9), rgba(6,10,18,0))',
};

const panel: React.CSSProperties = {
  position: 'absolute',
  background: 'rgba(8,14,24,0.9)',
  border: '1px solid rgba(127,212,255,0.22)',
  padding: '10px 14px',
  fontSize: 13,
  lineHeight: 1.6,
};

const label: React.CSSProperties = {
  fontSize: 10,
  opacity: 0.45,
  textTransform: 'uppercase',
  letterSpacing: 1.4,
  marginBottom: 4,
};

const tab = (active: boolean): React.CSSProperties => ({
  background: active ? 'rgba(127,212,255,0.18)' : 'rgba(8,14,24,0.82)',
  border: '1px solid rgba(127,212,255,0.22)',
  borderRight: 'none',
  color: active ? '#dbeaff' : 'rgba(219,234,255,0.7)',
  font: 'inherit',
  fontSize: 12,
  padding: '7px 18px',
  cursor: 'pointer',
});

/** A row on the station's shelf: what it is, and how many are left. */
const shelfRow = (chosen: boolean, out: boolean): React.CSSProperties => ({
  ...action,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  marginTop: 3,
  border: 'none',
  background: chosen ? 'rgba(127,212,255,0.18)' : 'transparent',
  cursor: out ? 'not-allowed' : 'pointer',
});

const action: React.CSSProperties = {
  background: 'rgba(8,14,24,0.82)',
  border: '1px solid rgba(127,212,255,0.22)',
  color: '#dbeaff',
  font: 'inherit',
  fontSize: 12,
  padding: '7px 14px',
  cursor: 'pointer',
};
